import type {
  DesignActionId,
  DesignDeleteTarget,
  DesignSystemMention,
} from "@afar-research/contracts";

import { designActionLabel, formatDesignEvidenceUrlLine } from "./designActions.ts";
import type { DesignAgentOperationSubmission } from "./designActions.ts";

/** The Design Operation the composer's Design Action is set to. */
export type DesignComposerOperation = DesignActionId;

/**
 * How a Design Operation presents itself in the composer and what it collects
 * besides text. One descriptor per operation so the Design chip's variant menu,
 * the headline, the placeholder, and the text-disabled description read from a
 * single list rather than four parallel switch statements in the UI.
 */
export interface DesignComposerOperationDescriptor {
  readonly id: DesignComposerOperation;
  /** The picker row and the composer's headline. */
  readonly label: string;
  /** What the Design chip wears, and what a pending card falls back to. */
  readonly shortLabel: string;
  /** What the mode does, said as an action rather than a name. */
  readonly headline: string;
  /** One line in the picker row, dropped once the composer is too narrow. */
  readonly blurb: string;
  /**
   * What the mode collects besides text. `target` is exactly one Design System
   * Mention; `targets` is Delete's own multi-select picker; `none` is Create,
   * whose display name is the text itself.
   */
  readonly collects: "none" | "target" | "targets";
  /**
   * Whether the operation preserves material the composer has to be holding.
   * `"required"` for Add Example alone: an Example with nothing attached and no
   * URL has nothing to preserve, so its send is gated on evidence rather than
   * on what was typed about it. The same flag puts `Add ▸ URL` in the mode's
   * Add menu — one place to say which operation takes evidence, so the menu
   * that collects it and the gate that demands it cannot disagree.
   */
  readonly evidence: "none" | "required";
  /**
   * What runs the operation. Delete alone is `"host"`: it never reaches the
   * Thread Queue and no Agent Provider turn runs. This is the picker's grouping
   * — Delete is separated by kind, not by how the seven happen to divide.
   */
  readonly backing: "agent" | "host";
  readonly typing:
    | { readonly kind: "enabled"; readonly placeholder: string }
    | { readonly kind: "disabled"; readonly description: string };
}

/**
 * Overflow-menu order: the five library operations that build a Design System, then
 * the two that change its identity or remove it. Delete keeps equal billing —
 * hiding a destructive operation behind a disclosure makes it harder to find
 * the way out of a Design System, not harder to fire by accident.
 */
const DESIGN_COMPOSER_OPERATION_ORDER: ReadonlyArray<DesignComposerOperation> = [
  "create-design",
  "update-design",
  "add-design-template",
  "add-design-example",
  "create-design-sample",
  "rename-design-system",
  "delete-design-system",
];

/**
 * How every placeholder that collects a target opens. A composer that cannot
 * send says why in the box the user is already looking at, and the target is
 * the precondition the five target modes share — so the instruction is written
 * once. Five placeholders each phrasing it their own way teaches five things
 * where there is one.
 *
 * It names no mechanism. The picker sits in the composer's footer with the
 * mode's own target on it, so a placeholder spelling out `@` taught the
 * shortcut as if it were the only way in.
 */
const PICK_TARGET = "Pick the Design System";

const DESIGN_COMPOSER_OPERATIONS: Readonly<
  Record<DesignComposerOperation, DesignComposerOperationDescriptor>
> = {
  "create-design": {
    id: "create-design",
    label: designActionLabel("create-design"),
    shortLabel: "Create",
    headline: "Create a Design System",
    blurb: "Writes one DESIGN.md as the sole canonical authority.",
    collects: "none",
    evidence: "none",
    backing: "agent",
    typing: {
      kind: "enabled",
      placeholder: "Type a name for the Design System, and what it is for…",
    },
  },
  "update-design": {
    id: "update-design",
    label: designActionLabel("update-design"),
    shortLabel: "Update",
    headline: "Update a Design System",
    blurb: "Revises DESIGN.md while preserving its storage identity.",
    collects: "target",
    evidence: "none",
    backing: "agent",
    typing: {
      kind: "enabled",
      placeholder: `${PICK_TARGET} and say what should change…`,
    },
  },
  "add-design-template": {
    id: "add-design-template",
    label: designActionLabel("add-design-template"),
    shortLabel: "Template",
    headline: "Add a Design Template",
    blurb: "Adds a reusable Template under a Design System.",
    collects: "target",
    evidence: "none",
    backing: "agent",
    typing: {
      kind: "enabled",
      placeholder: `${PICK_TARGET}, then say what the template is for (optional)…`,
    },
  },
  "add-design-example": {
    id: "add-design-example",
    label: designActionLabel("add-design-example"),
    shortLabel: "Example",
    headline: "Add a Design Example",
    blurb: "Preserves evidence — attach the files or paste a URL.",
    collects: "target",
    evidence: "required",
    backing: "agent",
    // Names both preconditions rather than inviting prose: Add Example is the
    // one mode whose send needs a target *and* evidence, and a placeholder that
    // asked only what the example shows left the send button dark with nothing
    // on screen saying what was missing.
    typing: {
      kind: "enabled",
      placeholder: `${PICK_TARGET} and attach at least one file or URL…`,
    },
  },
  "create-design-sample": {
    id: "create-design-sample",
    label: designActionLabel("create-design-sample"),
    shortLabel: "Sample",
    headline: "Create a Design Sample",
    blurb: "Produces a self-contained artifact from a Design System.",
    collects: "target",
    evidence: "none",
    backing: "agent",
    typing: {
      kind: "enabled",
      placeholder: `${PICK_TARGET}, then say what the sample should be…`,
    },
  },
  "rename-design-system": {
    id: "rename-design-system",
    label: designActionLabel("rename-design-system"),
    shortLabel: "Rename",
    headline: "Rename a Design System",
    blurb: "Moves the directory, rewrites DESIGN.md, and follows the slug through every artifact.",
    collects: "target",
    evidence: "none",
    backing: "agent",
    typing: {
      kind: "enabled",
      placeholder: `${PICK_TARGET}, then say what it should be called (optional)…`,
    },
  },
  "delete-design-system": {
    id: "delete-design-system",
    label: designActionLabel("delete-design-system"),
    shortLabel: "Delete",
    headline: "Delete Design Systems and artifacts",
    blurb: "Destructive. Removes the selection from the filesystem.",
    collects: "targets",
    evidence: "none",
    backing: "host",
    typing: {
      kind: "disabled",
      description:
        "Removes the selected Design Systems and artifact groups from the filesystem. The agent is not involved, and the deletion is left unstaged. Nothing to type — choose what to remove in the picker, then send.",
    },
  },
};

/** Every mode, in picker order. */
export function listDesignComposerOperations(): ReadonlyArray<DesignComposerOperationDescriptor> {
  return DESIGN_COMPOSER_OPERATION_ORDER.map((mode) => DESIGN_COMPOSER_OPERATIONS[mode]);
}

export function describeDesignComposerOperation(
  mode: DesignComposerOperation,
): DesignComposerOperationDescriptor {
  return DESIGN_COMPOSER_OPERATIONS[mode];
}

/**
 * Whether the composer can send in this mode. A target is a precondition rather
 * than a validation: burning a provider turn to learn you forgot to type `@` is
 * the worst version of this, and the Skill Bundles keep their own defences for
 * the App Control Tool Surface and CLI paths, which have no composer.
 *
 * An unpicked composer is an ordinary one and sends on its content alone. It is
 * a state the user reaches on purpose — stepping away from the composer unpicks
 * the operation — so refusing to send from it would strand every reply the
 * agent asks for.
 */
export function designComposerOperationCanSend(input: {
  readonly mode: DesignComposerOperation | null;
  /** Design System Mentions selected as the target, or Delete's picked targets. */
  readonly collectedCount: number;
  /** Attachments and evidence URLs, per `countDesignComposerEvidence`. */
  readonly evidenceCount: number;
  readonly hasComposerContent: boolean;
}): boolean {
  if (input.mode === null) return input.hasComposerContent;
  const { collects, evidence } = describeDesignComposerOperation(input.mode);
  // Create's text *is* the display name, so it is the one agent-backed mode
  // gated on content rather than on a target.
  if (collects === "none") return input.hasComposerContent;
  if (collects === "targets") return input.collectedCount > 0;
  // Add Example takes a target *and* something to preserve. Its text stays
  // optional — the evidence is the operation, and describing an Example the
  // App never received is the send that reads as working and does nothing.
  if (evidence === "required" && input.evidenceCount === 0) return false;
  return input.collectedCount === 1;
}

/**
 * What a Design composer's input invites, or `null` to leave the App's own
 * placeholder speaking: an unpicked composer is an ordinary composer, and a
 * mode whose input is off has its description drawn over the input instead.
 */
export function designComposerPlaceholder(mode: DesignComposerOperation | null): string | null {
  if (mode === null) return null;
  const { typing } = describeDesignComposerOperation(mode);
  return typing.kind === "enabled" ? typing.placeholder : null;
}

/**
 * What a Design composer has gathered besides its text. Design collects far
 * less than Brain: the four library operations and Rename take exactly one
 * Design System Mention, Create takes none, and only Delete has a picker of its
 * own. Attachments ride the composer's ordinary attachment surface, unchanged —
 * folders included, because the Skill Bundle judges what it received far better
 * than an extension allowlist can.
 */
export interface DesignComposerCollection {
  /**
   * The single target, for every mode whose `collects` is `"target"`. Picking a
   * second Design System replaces the first — single-select, the way a picker
   * behaves — so there is no two-target error to design.
   */
  readonly target: DesignSystemMention | null;
  /**
   * Delete's own multi-select picker. Never a Design System Mention: a mention
   * is a path, and a delete needs the revision it was listed at to be refusable
   * when the Design System moved on, so these are the wire's `DesignDeleteTarget`
   * as the picker read them out of `design.listSystems`.
   */
  readonly deleteTargets: ReadonlyArray<DesignDeleteTarget>;
  /**
   * Add Example's `Add ▸ URL` entries. Evidence that has no file to attach
   * still has a location, and the submission carries it as brief text rather
   * than as an attachment: nothing here is fetched, and the attachment contract
   * stays exactly as wide as ADR 0109 left it.
   */
  readonly evidenceUrls: ReadonlyArray<string>;
  /**
   * How many attachments the composer's ordinary attachment surface is holding.
   * A count rather than the attachments themselves: nothing here reads their
   * contents, and Add Example's gate has to weigh a dropped file against a
   * pasted URL, which means both have to be visible in one place.
   */
  readonly attachmentCount: number;
}

export const EMPTY_DESIGN_COMPOSER_COLLECTION: DesignComposerCollection = {
  target: null,
  deleteTargets: [],
  evidenceUrls: [],
  attachmentCount: 0,
};

/**
 * What the composer's Design System picker offers, or `null` when the mode has
 * nothing to point at — Create names its Design System in the text, and an
 * unpicked composer is an ordinary one.
 *
 * Lives beside `collects` because it is the same fact read out loud: the five
 * target modes take one Design System and Delete takes a set of systems and
 * artifact groups. A control that decided this for itself would be free to
 * offer a multi-select in a mode whose submission can only carry one.
 */
export type DesignComposerTargetPicker = {
  readonly kind: "single" | "multi";
  /** What the trigger wears: the pick itself once there is one. */
  readonly label: string;
  readonly picked: boolean;
};

export function describeDesignComposerTargetPicker(input: {
  readonly mode: DesignComposerOperation | null;
  readonly collection: DesignComposerCollection;
}): DesignComposerTargetPicker | null {
  if (input.mode === null) return null;
  const { collects } = describeDesignComposerOperation(input.mode);
  if (collects === "none") return null;
  if (collects === "targets") {
    const count = input.collection.deleteTargets.length;
    return {
      kind: "multi",
      label: count === 0 ? "Choose what to remove" : `${count} selected`,
      picked: count > 0,
    };
  }
  return {
    kind: "single",
    label: input.collection.target?.name ?? "Design System",
    picked: input.collection.target !== null,
  };
}

/**
 * Whether `Add ▸ URL` belongs in this mode's Add menu. Add Example alone: it is
 * the one operation whose material may live at a URL instead of on disk, and
 * the menu is the only place the old dialog's URL chip had left to go.
 */
export function designComposerOperationCollectsEvidenceUrls(
  mode: DesignComposerOperation,
): boolean {
  return describeDesignComposerOperation(mode).evidence !== "none";
}

/**
 * Whether the composer's attachment surface has anything to offer in this mode.
 *
 * Delete is the one that does not, and the reason is its backing rather than a
 * list of exceptions: no Agent Provider turn runs for it, so an attached file
 * would ride nothing. A paperclip that can only ever be disabled is a dead
 * control, so the surface asks this before drawing one.
 */
export function designComposerOperationTakesAttachments(mode: DesignComposerOperation): boolean {
  return describeDesignComposerOperation(mode).backing === "agent";
}

/**
 * The Design System Mentions a composer holds after picking one, given the mode
 * it is in. Every target mode is single-select, so a second pick *replaces* the
 * first rather than adding a target the operation could not use; off a Design
 * thread, and in a mode that collects no target, mentions stay the ordinary
 * multi-select context they are everywhere else in the App.
 */
export function selectDesignComposerMentions(input: {
  readonly mode: DesignComposerOperation | null;
  readonly existing: ReadonlyArray<DesignSystemMention>;
  readonly picked: DesignSystemMention;
}): ReadonlyArray<DesignSystemMention> {
  if (input.mode !== null && describeDesignComposerOperation(input.mode).collects === "target") {
    return [input.picked];
  }
  if (input.existing.some((mention) => mention.path === input.picked.path)) return input.existing;
  return [...input.existing, input.picked];
}

/** What `designComposerOperationCanSend` counts. Create collects nothing. */
export function countDesignComposerCollected(
  mode: DesignComposerOperation,
  collection: DesignComposerCollection,
): number {
  const { collects } = describeDesignComposerOperation(mode);
  if (collects === "targets") return collection.deleteTargets.length;
  if (collects === "target") return collection.target === null ? 0 : 1;
  return 0;
}

/**
 * What `designComposerOperationCanSend` counts as evidence: anything the send would
 * actually carry to the Skill Bundle, whether it came off the filesystem or out
 * of `Add ▸ URL`. Modes that require no evidence never read it.
 */
export function countDesignComposerEvidence(collection: DesignComposerCollection): number {
  return (
    collection.attachmentCount +
    collection.evidenceUrls.filter((url) => url.trim().length > 0).length
  );
}

/**
 * The submission a send in this mode carries, or `null` when the collection
 * cannot make a valid one. This and `designComposerOperationCanSend` read the same
 * collection, so a composer that says it can send always produces a payload.
 *
 * Delete is absent: it never becomes a queued submission, fanning out to one
 * host RPC per target instead, so its payload is not built here.
 */
export function buildDesignComposerSubmission(input: {
  readonly mode: DesignComposerOperation;
  readonly text: string;
  readonly collection: DesignComposerCollection;
}): DesignAgentOperationSubmission | null {
  const text = input.text.trim();
  const brief = text.length > 0 ? { brief: text } : {};
  const target = input.collection.target;
  // Add Example's URLs fold into the brief, one line each, the way the retired
  // dialog wrote them. The Skill Bundle reads a brief; giving evidence URLs a
  // field of their own on the wire would only give it a second place to look.
  const exampleBriefParts = [
    ...(text.length > 0 ? [text] : []),
    ...input.collection.evidenceUrls
      .map((url) => url.trim())
      .filter((url) => url.length > 0)
      .map(formatDesignEvidenceUrlLine),
  ];
  const exampleBrief = exampleBriefParts.length > 0 ? { brief: exampleBriefParts.join("\n") } : {};

  switch (input.mode) {
    case "create-design":
      // The text is the display name, not a brief: Create has nothing to point
      // at, so the one thing it collects is what the Design System is called.
      return text.length === 0 ? null : { action: "create-design", displayName: text };
    case "update-design":
      return target === null ? null : { action: "update-design", target, ...brief };
    case "add-design-template":
      return target === null ? null : { action: "add-design-template", target, ...brief };
    case "add-design-example":
      // The same rule the send button reads, reached through a keybinding or a
      // queued dispatch: an Example with nothing to preserve is not a send.
      return target === null || countDesignComposerEvidence(input.collection) === 0
        ? null
        : { action: "add-design-example", target, ...exampleBrief };
    case "create-design-sample":
      return target === null ? null : { action: "create-design-sample", target, ...brief };
    case "rename-design-system":
      // A brief, like every other target operation: the skill establishes the
      // new name with the user and derives the slug from it. Reading the text
      // as the name meant a sentence describing the rename became the name.
      return target === null ? null : { action: "rename-design-system", target, ...brief };
    case "delete-design-system":
      return null;
  }
}
